import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { resignationAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  FileText, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ArrowRight,
  MoreVertical,
  LogOut,
  Calendar
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { useNavigate } from 'react-router-dom';

const ResignationManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [dialogMode, setDialogMode] = useState(null); // 'approve' or 'reject'
  const navigate = useNavigate();

  const fetchRequests = async () => {
    try {
      const response = await resignationAPI.getAll();
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch resignation requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAction = async () => {
    try {
      if (dialogMode === 'approve') {
        await resignationAPI.approve(selectedRequest.id, { remarks });
        toast.success('Resignation approved. You can now process FnF.');
      } else {
        await resignationAPI.reject(selectedRequest.id, { remarks });
        toast.success('Resignation rejected.');
      }
      setDialogMode(null);
      setSelectedRequest(null);
      setRemarks('');
      fetchRequests();
    } catch (error) {
      toast.error(`Failed to ${dialogMode} request`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-700 border-none shadow-none">Approved</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-700 border-none shadow-none">Rejected</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700 border-none shadow-none">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Resignation Management</h1>
          <p className="text-muted-foreground">Manage employee exit requests and transitions.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-blue-50 border-blue-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-blue-600 font-medium uppercase text-[10px] tracking-wider">Pending Exit</CardDescription>
            <CardTitle className="text-2xl text-blue-900">{requests.filter(r => r.status === 'PENDING').length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-green-50 border-green-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-green-600 font-medium uppercase text-[10px] tracking-wider">Approved This Month</CardDescription>
            <CardTitle className="text-2xl text-green-900">{requests.filter(r => r.status === 'APPROVED').length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-slate-50 border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-slate-600 font-medium uppercase text-[10px] tracking-wider">Total Lifetime</CardDescription>
            <CardTitle className="text-2xl text-slate-900">{requests.length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/50">
              <TableRow>
                <TableHead className="w-[250px]">Employee</TableHead>
                <TableHead>Applied On</TableHead>
                <TableHead>Last Working Day</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">Loading requests...</TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground italic">No resignation requests found.</TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-slate-50/50 transition-colors">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900">{request.employee_name}</span>
                        <span className="text-[11px] text-muted-foreground uppercase">{request.employee_code || request.employee_id}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 italic text-sm">
                      {format(new Date(request.applied_at), 'dd MMM, yyyy')}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-indigo-500" />
                        {format(new Date(request.last_working_day), 'dd MMM, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600 text-sm line-clamp-1 max-w-[200px]" title={request.reason}>
                        {request.reason}
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="text-right">
                      {request.status === 'PENDING' ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border-none h-8"
                            onClick={() => {
                              setSelectedRequest(request);
                              setDialogMode('reject');
                            }}
                          >
                            Reject
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-indigo-600 text-white hover:bg-indigo-700 h-8 shadow-sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setDialogMode('approve');
                            }}
                          >
                            Approve
                          </Button>
                        </div>
                      ) : request.status === 'APPROVED' ? (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 h-8 font-semibold shadow-sm"
                          onClick={() => navigate('/fnf', { 
                            state: { 
                              employee_id: request.employee_id, 
                              exit_date: request.last_working_day.split('T')[0],
                              reason: request.reason 
                            } 
                          })}
                        >
                          Process FnF <ArrowRight className="ml-2 h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Processed</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approve/Reject Dialog */}
      <Dialog open={dialogMode !== null} onOpenChange={(open) => !open && setDialogMode(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialogMode === 'approve' ? (
                <><CheckCircle2 className="h-5 w-5 text-green-500" /> Approve Resignation</>
              ) : (
                <><XCircle className="h-5 w-5 text-red-500" /> Reject Resignation</>
              )}
            </DialogTitle>
            <DialogDescription>
              Resignation request for <span className="font-semibold text-slate-900">{selectedRequest?.employee_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Remarks</label>
              <Textarea 
                placeholder="Add comments for the employee..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogMode(null)}>Cancel</Button>
            <Button 
              className={dialogMode === 'approve' ? 'bg-indigo-600' : 'bg-red-600'}
              onClick={handleAction}
            >
              Confirm {dialogMode === 'approve' ? 'Approval' : 'Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResignationManagement;
